import type { AnalyticsSummary, BotConfigResponse, BotEventType } from '@bale-bot/types';
import {
  Alert,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import BarRow from '../components/BarRow';
import DailyBars from '../components/DailyBars';
import PageHeader from '../components/PageHeader';
import SectionCard from '../components/SectionCard';
import StatTile from '../components/StatTile';
import { api, describeError } from '../services/api';
import { actionToLabel, buildActionLabelMap } from '../utils';

const RANGES = [7, 30, 90];

const EVENT_LABELS: Record<BotEventType, string> = {
  COMMAND_START: 'Started the bot',
  MENU_SELECTED: 'Menu selection',
  ROUTED_TO_AM: 'Routed to a manager',
  ROUTED_TO_CHAT: 'Routed to live chat',
  SESSION_ENDED: 'Session ended',
  FALLBACK: 'Fallback reply',
  ERROR: 'Error',
};

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [config, setConfig] = useState<BotConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset when the range changes (during render – this is the allowed pattern)
  const [prevDays, setPrevDays] = useState(days);
  if (days !== prevDays) {
    setPrevDays(days);
    setSummary(null);
    setError(null);
  }

  useEffect(() => {
    let active = true;

    Promise.all([api.getAnalytics(days), api.getSettings()])
      .then(([analytics, settings]) => {
        if (!active) return;
        setSummary(analytics);
        setConfig(settings);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeError(cause));
      });

    return () => {
      active = false;
    };
  }, [days]);

  const maxEventType = summary?.byEventType.reduce((acc, row) => Math.max(acc, row.count), 0) ?? 0;
  const maxManager = summary?.managerLoad.reduce((acc, row) => Math.max(acc, row.count), 0) ?? 0;
  const labels = useMemo(
    () => buildActionLabelMap(config?.customMenu),
    [config?.customMenu],
  );

  const maxButtonClick =
    summary?.buttonClicks.reduce((acc, row) => Math.max(acc, row.count), 0) ?? 0;
  return (
    <Stack spacing={3} className="stagger">
      <PageHeader
        title="Analytics"
        description="Bot activity from the event log."
        actions={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={days}
            onChange={(_event, value: number | null) => {
              if (value) setDays(value);
            }}
            sx={{
              bgcolor: (t) => t.palette.background.paper,
              backdropFilter: 'blur(12px)',
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: 2,
              '& .MuiToggleButton-root': {
                border: 0,
                px: 1.75,
                '&.Mui-selected': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.main' },
                },
              },
            }}
          >
            {RANGES.map((range) => (
              <ToggleButton key={range} value={range}>
                {range}d
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
      />

      {error && <Alert severity="error">{error}</Alert>}
      {!summary && !error && <CircularProgress size={28} />}

      {summary && (
        <>
          <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label="Total events" value={summary.totalEvents} caption={`last ${days} days`} />
            <StatTile label="Unique users" value={summary.uniqueUsers} caption="distinct Bale ids" />
            <StatTile label="Routed to a manager" value={summary.routedToAccountManager} />
            <StatTile label="Routed to live chat" value={summary.routedToLiveChat} />
          </Stack>

          <SectionCard title="Daily event volume" description="One series — peak day is labelled.">
            <DailyBars data={summary.daily} />
          </SectionCard>

          <SectionCard title="Events by type">
            {summary.byEventType.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No events in this range yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {summary.byEventType.map((row) => (
                  <BarRow
                    key={row.eventType}
                    label={EVENT_LABELS[row.eventType] ?? row.eventType}
                    value={row.count}
                    max={maxEventType}
                  />
                ))}
              </Stack>
            )}
          </SectionCard>
          <SectionCard
            title="Button clicks"
            description="How often each menu action was selected."
          >
            {summary.buttonClicks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No button clicks in this range yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {summary.buttonClicks.map((row) => (
                  <BarRow
                    key={row.action}
                    label={actionToLabel(row.action, labels)}
                    value={row.count}
                    max={maxButtonClick}
                  />
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard
            title="Inquiries per account manager"
            description="Uneven counts are expected when managers were added or removed mid-range."
          >
            {summary.managerLoad.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No inquiries have been routed to a manager yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {summary.managerLoad.map((row) => {
                  const rowWithName = row as typeof row & { username?: string };
                  const label = rowWithName.username
                    ? `@${rowWithName.username}`
                    : row.managerId
                      ? `User ${row.managerId}`
                      : 'Unknown manager';
                  return (
                    <BarRow
                      key={rowWithName.username ?? row.managerId}
                      label={label}
                      value={row.count}
                      max={maxManager}
                    />
                  );
                })}
              </Stack>
            )}
          </SectionCard>
        </>
      )}
    </Stack>
  );
}